import React, {useState, useContext, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Modal, Dialogue, Button, Text, Icon, addToast} from '@albert-io/atomic';
import {query} from '@albert-io/json-api-framework/request/builder';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import {getModelForResourceType} from 'resources/modelRegistry';
import {history} from 'client/history';

import PeopleContext from '../../people.context';

const RemoveCoteacher = ({teacher}) => {
  const {currentTeacherId} = useContext(PeopleContext);
  const [showRemoveCoteacherModal, setShowRemoveCoteacherModal] = useState(false);
  const toggleShowCoteacherModal = () => setShowRemoveCoteacherModal(!showRemoveCoteacherModal);
  const isRemovingSelf = useMemo(
    () => teacher.getId() === currentTeacherId,
    [currentTeacherId, teacher]
  );
  return (
    <>
      <Button
        color='destructive'
        variant='text'
        size='s'
        onClick={() => setShowRemoveCoteacherModal(true)}
      >
        {isRemovingSelf ? 'Remove Yourself' : 'Remove'}
      </Button>
      {showRemoveCoteacherModal ? (
        <RemoveCoteacherModal
          teacher={teacher}
          handleClose={toggleShowCoteacherModal}
          isRemovingSelf={isRemovingSelf}
        />
      ) : null}
    </>
  );
};

RemoveCoteacher.propTypes = {
  teacher: PropTypes.instanceOf(getModelForResourceType('teacher_v1'))
};

const RemoveCoteacherModal = ({handleClose, teacher, isRemovingSelf}) => {
  const [isRequestPending, setIsRequestPending] = useState(false);
  const {classroom, queries, currentTeacherId} = useContext(PeopleContext);
  async function removeCoteacher() {
    setIsRequestPending(true);
    const email = teacher.getEmail();
    try {
      await genericMandarkRequest(
        'delete',
        query()
          .mandarkEndpoint(['classrooms_v1', classroom.getId(), 'relationships', 'teachers_v1'])
          .done(),
        {
          data: [
            {
              id: teacher.getId()
            }
          ]
        }
      );

      invalidatePartialInterest({
        resourcePath: queries.coteachers.done().resourcePath
      });

      // redirect back to classes if self deleting, otherwise removed teacher will still be able to remove co-teachers
      if (isRemovingSelf) {
        // invalidate classrooms list query to remove old classroom
        invalidatePartialInterest({
          resourcePath: ['teachers_v1', currentTeacherId, 'classrooms_v1']
        });
        history.push('/classes');
      }

      addToast({
        type: 'success',
        title: 'Success',
        message: `${email} has been removed from this classroom.`
      });
    } catch (err) {
      addToast({
        type: 'danger',
        title: 'Error',
        message: `There was a problem removing ${email} from this classroom. Please try again.`
      });
    } finally {
      handleClose();
    }
  }
  return (
    <Modal ariaLabel='Remove co-teacher' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            paddingLevel={3}
            inModal
            className={modalContentStyle}
            hideCloseBtn
            handleClose={handleClose}
            size='s'
          >
            <Dialogue.Title align='center'>
              <span className='remove-coteacher__warning-icon-wrapper'>
                <Icon icon={['fal', 'octagon-exclamation']} color='danger' />
              </span>
            </Dialogue.Title>
            <Dialogue.Body>
              <Text as='p' className='u-mar-b_2' align='center'>
                {isRemovingSelf
                  ? `Are you sure you want to remove yourself from "${classroom.getName()}"?`
                  : 'Removing this co-teacher will remove their access to this classroom.'}
              </Text>
            </Dialogue.Body>
            <Dialogue.BtnGroup direction='column' fillColumn>
              <Button
                disabled={isRequestPending}
                onClick={removeCoteacher}
                color='destructive'
                variant='outlined'
              >
                {isRemovingSelf ? 'Remove Me From This Classroom' : 'Remove co-teacher'}
              </Button>
              <CloseButtonWrapper>
                <Button
                  disabled={isRequestPending}
                  type='button'
                  color='secondary'
                  variant='outlined'
                >
                  Cancel
                </Button>
              </CloseButtonWrapper>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

RemoveCoteacherModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  teacher: PropTypes.instanceOf(getModelForResourceType('teacher_v1')),
  isRemovingSelf: PropTypes.bool
};

export default RemoveCoteacher;
